import * as Highcharts from 'highcharts';
import {
  CHART_TOOLTIP_BG_OVER_GRAPH,
  CHART_TOOLTIP_BG_OVER_LEGEND,
  CHART_TOOLTIP_CLASS,
  CHART_TOOLTIP_HORIZONTAL_OFFSET,
  CHART_TOOLTIP_VIEWPORT_MARGIN,
} from './chart-tooltip-positioner.constants';
import {
  ChartTooltipColors,
  ChartTooltipHeightReservation,
  ChartTooltipPositionerOptions,
  ChartTooltipWithContainer,
} from './chart-tooltip-positioner.types';

const DEFAULT_CHART_TOOLTIP_COLORS: ChartTooltipColors = {
  overGraph: CHART_TOOLTIP_BG_OVER_GRAPH,
  overLegend: CHART_TOOLTIP_BG_OVER_LEGEND,
};

export function buildChartTooltipBackground(
  splitPct: number,
  { overGraph, overLegend }: ChartTooltipColors = DEFAULT_CHART_TOOLTIP_COLORS
): string {
  return `linear-gradient(to bottom, ${overGraph} 0%, ${overGraph} ${splitPct}%, ${overLegend} ${splitPct}%, ${overLegend} 100%)`;
}

export function buildChartTooltipPositioner(
  getLastHtml: () => string,
  setSplitPct: (pct: number) => void = () => undefined,
  options: ChartTooltipPositionerOptions = {}
): Highcharts.TooltipPositionerCallbackFunction {
  const reservations = new WeakMap<
    Highcharts.Chart,
    ChartTooltipHeightReservation
  >();

  return function (_labelWidth, _labelHeight, point) {
    const chart = this.chart;
    const chartRect = chart.container.getBoundingClientRect();
    const { width, height } = measureTooltipHtml(getLastHtml());

    const seriesKey = tooltipSeriesKey(chart);
    const reservation = reservations.get(chart);
    const reservedHeight =
      reservation?.seriesKey === seriesKey
        ? Math.max(reservation.height, height)
        : height;
    reservations.set(chart, { seriesKey, height: reservedHeight });

    const y = resolveTooltipY(chart, chartRect, reservedHeight, options);
    const x = resolveTooltipX(chart, chartRect, point, width);

    if (height > 0) {
      const graphBottom = chart.plotTop + chart.plotHeight;
      const splitPct = Math.round(
        Math.max(0, Math.min(1, (graphBottom - y) / height)) * 100
      );
      setSplitPct(splitPct);
      applyTooltipBackground(
        this as ChartTooltipWithContainer,
        splitPct,
        options.getColors?.(chart)
      );
    }

    return { x, y };
  };
}

function resolveTooltipY(
  chart: Highcharts.Chart,
  chartRect: DOMRect,
  reservedHeight: number,
  {
    aboveChartGap = CHART_TOOLTIP_VIEWPORT_MARGIN,
    boundarySelector,
  }: ChartTooltipPositionerOptions
): number {
  const spaceBelow =
    window.innerHeight - chartRect.bottom + CHART_TOOLTIP_VIEWPORT_MARGIN;
  const spaceAbove = chartRect.top;

  if (spaceBelow < reservedHeight && spaceAbove > spaceBelow) {
    const y =
      chartRect.top < 0
        ? chartRect.top + reservedHeight / 2
        : -reservedHeight - aboveChartGap;
    return Math.max(resolveUpperBoundY(chart, chartRect, boundarySelector), y);
  }

  const y = chart.chartHeight;
  if (
    chartRect.top + y + reservedHeight >
    window.innerHeight - CHART_TOOLTIP_VIEWPORT_MARGIN
  ) {
    return (
      window.innerHeight -
      CHART_TOOLTIP_VIEWPORT_MARGIN -
      chartRect.top -
      reservedHeight
    );
  }
  return y;
}

function resolveUpperBoundY(
  chart: Highcharts.Chart,
  chartRect: DOMRect,
  boundarySelector?: string
): number {
  const boundary = boundarySelector
    ? chart.container.closest(boundarySelector)
    : null;
  const boundaryTop = boundary?.getBoundingClientRect().top ?? 0;

  return Math.max(0, boundaryTop) - chartRect.top;
}

function resolveTooltipX(
  chart: Highcharts.Chart,
  chartRect: DOMRect,
  point: Highcharts.TooltipPositionerPointObject,
  width: number
): number {
  const minX = -chartRect.left + CHART_TOOLTIP_VIEWPORT_MARGIN;
  const maxX =
    window.innerWidth - chartRect.left - width - CHART_TOOLTIP_VIEWPORT_MARGIN;

  let x = point.plotX + chart.plotLeft + CHART_TOOLTIP_HORIZONTAL_OFFSET;
  if (x > maxX) {
    x = point.plotX + chart.plotLeft - CHART_TOOLTIP_HORIZONTAL_OFFSET - width;
  }
  return Math.max(0, Math.min(Math.max(minX, x), maxX));
}

function measureTooltipHtml(html: string): { width: number; height: number } {
  if (!html) return { width: 0, height: 0 };

  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;left:-9999px;top:-9999px;visibility:hidden;';
  probe.innerHTML = html;
  document.body.appendChild(probe);

  try {
    const target =
      probe.querySelector<HTMLElement>(`.${CHART_TOOLTIP_CLASS}`) ?? probe;
    const { width, height } = target.getBoundingClientRect();
    return { width, height };
  } finally {
    document.body.removeChild(probe);
  }
}

function tooltipSeriesKey(chart: Highcharts.Chart): string {
  return chart.series
    .filter(
      (series) =>
        series.visible &&
        (series.options as Highcharts.SeriesLineOptions).enableMouseTracking !==
          false
    )
    .map(({ name }) => name)
    .join('|');
}

function applyTooltipBackground(
  tooltip: ChartTooltipWithContainer,
  splitPct: number,
  colors?: ChartTooltipColors
): void {
  const root = tooltip.container?.querySelector<HTMLElement>(
    `.${CHART_TOOLTIP_CLASS}`
  );
  if (root) {
    root.style.background = buildChartTooltipBackground(splitPct, colors);
  }
}
